import React, { memo, useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Button, Flex, PasswordInput, Text
} from 'onefaceui-react-components';
import PropTypes from 'prop-types';

import PasswordValidator from 'react/components/PasswordValidator';

import { actionCreators } from 'react/api/actions/recoverPassword';
import { getResourceTo } from 'react/utils/utils';

function ChangePassword({
  isParentMenuOpen
}) {
  const dispatch = useDispatch();

  const changedPasswordSucessfully = useSelector(({ recoverPassword }) => recoverPassword.get('success'));
  const oldPassword = useSelector(({ recoverPassword }) => recoverPassword.getIn(['password', 'oldPass']));
  const newPassword = useSelector(({ recoverPassword }) => recoverPassword.getIn(['password', 'newPass']));
  const passwordConfirmation = useSelector(({ recoverPassword }) => recoverPassword.getIn(['password', 'confirmPass']));
  const hintsValidated = useSelector(({ recoverPassword }) => recoverPassword.getIn(['password', 'hintsValidated']));
  const isLoading = useSelector(({ recoverPassword }) => recoverPassword.get('loading'));
  const errorResource = useSelector(({ recoverPassword }) => recoverPassword.get('errorMessage'));

  const arePasswordsEquals = useMemo(
    () => newPassword && passwordConfirmation && newPassword === passwordConfirmation,
    [newPassword, passwordConfirmation]
  );

  const newPasswordIsSameOldPassword = useMemo(
    () => !!newPassword && !!oldPassword && newPassword === oldPassword,
    [newPassword, oldPassword]
  );

  const newPasswordAndConfirmPasswordDiff = useMemo(
    () => (!!newPassword && !!passwordConfirmation) && newPassword !== passwordConfirmation,
    [newPassword, passwordConfirmation]
  );

  const buttonEnabled = useMemo(
    () => arePasswordsEquals && hintsValidated
    && !newPasswordIsSameOldPassword && !newPasswordAndConfirmPasswordDiff,
    [
      arePasswordsEquals,
      hintsValidated,
      newPasswordIsSameOldPassword,
      newPasswordAndConfirmPasswordDiff
    ]
  );

  const saveButtonClassName = useMemo(
    () => (isLoading ? 'loader-spinner' : ''),
    [isLoading]
  );

  const errorMessage = useMemo(
    () => getResourceTo(errorResource),
    [errorResource]
  );

  const onChange = ({ target: { value, name } }) => {
    dispatch(actionCreators.onChange({ value, id: name }));
  };

  const allHintsValidated = (value) => {
    dispatch(actionCreators.onChange({ value, id: 'hintsValidated' }));
  };

  const handleSubmit = () => {
    const url = '/User/ChangePassword';
    const payload = {
      login: Monet.Session.User.login,
      oldPassword,
      password: newPassword,
      passwordConfirmation
    };

    dispatch(actionCreators.requestChangePassword({ url, payload }));
  };

  const resetReduxState = () => {
    setTimeout(() => {
      dispatch(actionCreators.resetState());
    }, 400);
  };

  useEffect(() => {
    if (!isParentMenuOpen && changedPasswordSucessfully) {
      resetReduxState();
    }
  }, [isParentMenuOpen, changedPasswordSucessfully]);

  return (
    <Flex flexDirection="column" gap={12} className="profileMenuChangePassword">
      <Flex shouldShow={!!errorResource}>
        <Text type="danger" size="xs">
          {errorMessage}
        </Text>
      </Flex>
      <Flex shouldShow={changedPasswordSucessfully}>
        <Text type="success" size="xs">
          <i className="checkIcon fa fa-check" />
          {' '}
          {getResourceTo('user.changePassword.successMessage')}
        </Text>
      </Flex>
      <Flex shouldShow={!changedPasswordSucessfully} flexDirection="column" gap={8}>
        <PasswordInput
          label={getResourceTo('user.changePassword.oldPassword')}
          name="oldPass"
          value={oldPassword}
          onChange={onChange}
          className="oneface-input"
        />
        <PasswordInput
          label={getResourceTo('user.changePassword.newPassword')}
          name="newPass"
          value={newPassword}
          onChange={onChange}
          className="oneface-input"
          errorMsg={newPasswordIsSameOldPassword && getResourceTo('user.changePassword.sameOldPassword')}
        />
        <PasswordValidator
          user={Monet.Session.User.login}
          maxPerCol={3}
          newPass={newPassword}
          allHintsValidated={allHintsValidated}
        />
        <PasswordInput
          label={getResourceTo('user.changePassword.confirmNewPassword')}
          name="confirmPass"
          value={passwordConfirmation}
          onChange={onChange}
          className="oneface-input"
          errorMsg={newPasswordAndConfirmPasswordDiff && getResourceTo('user.changePassword.diffNewPasswordAndConfirmation')}
        />
        <Button
          loading={saveButtonClassName}
          onClick={handleSubmit}
          disabled={!buttonEnabled}
        >
          {getResourceTo('users.button.save')}
        </Button>
      </Flex>
    </Flex>
  );
}

ChangePassword.propTypes = {
  isParentMenuOpen: PropTypes.bool.isRequired
};

export default memo(ChangePassword);
